// packages/app/src/scaffolder/ServiceRepoPicker.tsx
//
// Conditional field extension for create-service template Page 2.
// Reads serviceRepo selection from Page 1 via formContext and renders
// the correct input fields:
//   import     → existingRepo (text input)
//   fork       → sourceRepo + projectName + destinationRepo (3 text inputs)
//   do_nothing → nothing (info message only)
//
// Output value shape:
// {
//   action: 'import' | 'fork' | 'do_nothing',
//   existingRepo?: string,
//   sourceRepo?: string,
//   projectName?: string,
//   destinationRepo?: string,
// }

import { useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

interface RepoPickerValue {
  action:          'import' | 'fork' | 'do_nothing' | '';
  existingRepo?:   string;
  sourceRepo?:     string;
  projectName?:    string;
  destinationRepo?: string;
}

export const ServiceRepoPickerComponent = ({
  onChange,
  rawErrors,
  formData,
  formContext,
}: FieldExtensionComponentProps<RepoPickerValue>) => {

  // Read serviceRepo selection from Page 1
  const action = (formContext?.formData?.serviceRepo as string) ?? '';

  const val: RepoPickerValue = {
    action: action as RepoPickerValue['action'],
    existingRepo:    formData?.existingRepo    ?? '',
    sourceRepo:      formData?.sourceRepo      ?? '',
    projectName:     formData?.projectName     ?? '',
    destinationRepo: formData?.destinationRepo ?? '',
  };

  const update = (patch: Partial<RepoPickerValue>) => {
    onChange({ ...val, ...patch, action: action as RepoPickerValue['action'] });
  };

  // Sync action when formContext changes
  useEffect(() => {
    if (action !== val.action) {
      onChange({ ...val, action: action as RepoPickerValue['action'] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  const hasError = (rawErrors?.length ?? 0) > 0;

  const inputStyle = { marginTop: 16 };

  // ── do_nothing ─────────────────────────────────────────────────────────────
  if (action === 'do_nothing') {
    return (
      <FormControl fullWidth>
        <FormLabel style={{ fontSize: 14, marginBottom: 8 }}>Repository Action</FormLabel>
        <Typography variant="body2" style={{ color: '#888', marginTop: 8 }}>
          No repository action selected. The service will be saved to the database only.
        </Typography>
      </FormControl>
    );
  }

  // ── import ──────────────────────────────────────────────────────────────────
  if (action === 'import') {
    return (
      <FormControl fullWidth error={hasError}>
        <FormLabel style={{ fontSize: 14, marginBottom: 4 }}>Import Settings</FormLabel>
        <FormHelperText style={{ marginBottom: 8 }}>
          Provide the existing Bitbucket repo slug in the discounttire workspace.
          Pipeline variables TEAM_KEY, APP_KEY and SVC_KEY will be set automatically
          and pipelines will be enabled.
        </FormHelperText>

        <TextField
          label="Existing Repo Slug *"
          value={val.existingRepo ?? ''}
          onChange={e => update({ existingRepo: e.target.value })}
          placeholder="e.g. dt-java-test-api"
          helperText="The repo slug in discounttire workspace to import into"
          fullWidth
          style={inputStyle}
          variant="standard"
        />
      </FormControl>
    );
  }

  // ── fork ────────────────────────────────────────────────────────────────────
  if (action === 'fork') {
    return (
      <FormControl fullWidth error={hasError}>
        <FormLabel style={{ fontSize: 14, marginBottom: 4 }}>Fork Settings</FormLabel>
        <FormHelperText style={{ marginBottom: 8 }}>
          The source repo will be forked into the discounttire workspace.
          Pipeline variables TEAM_KEY, APP_KEY and SVC_KEY will be set automatically
          and pipelines will be enabled on the new repo.
        </FormHelperText>

        <TextField
          label="Source Repo Slug *"
          value={val.sourceRepo ?? ''}
          onChange={e => update({ sourceRepo: e.target.value })}
          placeholder="e.g. dt-java-template-api"
          helperText="The existing repo to fork from (in discounttire workspace)"
          fullWidth
          style={inputStyle}
          variant="standard"
        />

        <TextField
          label="Project Key *"
          value={val.projectName ?? ''}
          onChange={e => update({ projectName: e.target.value })}
          placeholder="e.g. BS"
          helperText="Bitbucket project key for the new forked repo"
          fullWidth
          style={inputStyle}
          variant="standard"
        />

        <TextField
          label="Destination Repo Name *"
          value={val.destinationRepo ?? ''}
          onChange={e => update({ destinationRepo: e.target.value })}
          placeholder="e.g. dt-java-test-api"
          helperText="Name for the new forked repository"
          fullWidth
          style={inputStyle}
          variant="standard"
        />
      </FormControl>
    );
  }

  // ── no selection yet ────────────────────────────────────────────────────────
  return (
    <FormControl fullWidth>
      <FormLabel style={{ fontSize: 14, marginBottom: 8 }}>Repository Action</FormLabel>
      <Typography variant="body2" style={{ color: '#aaa', marginTop: 8 }}>
        ← Go back and select a Service Repo option on Page 1
      </Typography>
    </FormControl>
  );
};
